/**
 * «دوستی که هنوز عضو نیست» — همون ردیف‌های `contacts` با `is_friend = true`.
 *
 * ═══ چرا توی core، وقتی مخاطبین کارِ موبایله ═══
 *
 * `contactsRepo` (در apps/mobile/src/native) عمداً موبایل‌مونده، چون به
 * دفترچه‌ی تلفنِ گوشی وابسته‌ست. ولی از مایگریشن 0075، دعوت کردنِ کسی به یه
 * سیگنال — چه با پیامک از موبایل، چه با ایمیل از **وب** — خودکار براش یه
 * ردیفِ `contacts` می‌سازه. یعنی حالا وب هم آدم‌هایی داره که عضو نیستن و
 * باید توی فهرستِ دوستانش دیده شن؛ بدونِ این فایل، همون آدم‌ها فقط روی
 * موبایل دیده می‌شدن و کاربرِ وب هیچ‌وقت نمی‌فهمید کی رو دعوت کرده.
 *
 * پس این‌جا **زیرمجموعه‌ی کوچکِ مشترک** است: خواندن، برداشتن، ویرایشِ
 * اسم/شماره/ایمیل (WHOSUP-144)، و افزودنِ دستی (WHOSUP-148). مچ کردن،
 * ایمپورت، عکس و شبکه‌های اجتماعی
 * همچنان مالِ `contactsRepo`ِ موبایله و این‌جا تکرار نمی‌شه.
 *
 * عکس عمداً برنمی‌گرده: باکتِ `contact-avatars` خصوصیه و آدرسِ امضاشده
 * می‌خواد؛ مخاطبی که از راهِ دعوت ساخته شده اصلاً عکسی نداره. حرفِ اولِ اسم
 * (که هر دو اپ برای Avatar دارن) همون کار رو می‌کنه بدونِ یه رفت‌وبرگشتِ
 * اضافه به سرور.
 */
import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { demoStore } from '../demo/store';
import { normalizeEmail, normalizePhone } from '../lib/contacts';
import { phoneRepo } from './phoneRepo';
import { reportError } from '../lib/report';
import type { UserId } from '../domain/types';

/** بعد از ساختن/ویرایش، مثل موبایل مچ دوباره اجرا می‌شه؛ شکستش کارِ اصلی رو نمی‌شکنه. */
async function rematch(): Promise<void> {
  const { error } = await supabase.rpc('match_my_contacts');
  if (error) reportError('contactFriendRepo.rematch', error);
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/** یه آدم توی فهرستِ دوستان که ردیفِ دوستیِ اپی نداره. */
export interface ContactFriend {
  id: number;
  name: string;
  /** راه‌های رسیدن؛ برای نشون دادنِ «چطور خبردار می‌شه». */
  emails: string[];
  phones: string[];
  /** پر باشه یعنی عضو شده و ردیفِ دوستی مسئولِ نشون دادنشه، نه این. */
  matchedUserId: UserId | null;
}

/** چیزی که فرمِ ویرایشِ وب می‌فرسته — همون سه فیلدی که `ContactFriend` نشون می‌ده. */
export interface ContactFriendEdit {
  name: string;
  phones: string[];
  emails: string[];
}

const uniq = (xs: (string | null)[]) => [...new Set(xs.filter((x): x is string => !!x))];

const map = (c: any): ContactFriend => ({
  id: c.id,
  // اسمِ خالی ممکنه: دعوتِ ایمیلی بدونِ نام. اون‌وقت خودِ آدرس اسمشه.
  name: c.display_name || (c.emails ?? [])[0] || (c.phones ?? [])[0] || '',
  emails: c.emails ?? [],
  phones: c.phones ?? [],
  matchedUserId: c.matched_user_id ?? null,
});

export const contactFriendRepo = {
  /** دوستانِ دفترچه‌ای من. سیاستِ `ct_all` فقط ردیف‌های خودم رو می‌ده. */
  async list(): Promise<ContactFriend[]> {
    if (isDemoMode) {
      return demoStore.contacts.list()
        .filter((c) => c.isFriend)
        .map((c) => ({
          id: c.id,
          name: c.displayName || c.emails[0] || c.phones[0] || '',
          emails: c.emails,
          phones: c.phones,
          matchedUserId: c.matchedUserId ?? null,
        }));
    }
    const { data, error } = await supabase
      .from('contacts')
      .select('id, display_name, emails, phones, matched_user_id')
      .eq('is_friend', true)
      .eq('hidden', false)
      .order('display_name');
    if (error) throw error;
    return (data ?? []).map(map);
  },

  /**
   * فقط تعدادشون — قرینه‌ی `friendRepo.count`، برای همون یه تصمیم
   * (نگاه کن به lib/noFriendsNotice).
   */
  async count(): Promise<number> {
    if (isDemoMode) return demoStore.contacts.list().filter((c) => c.isFriend).length;
    const { count, error } = await supabase
      .from('contacts')
      .select('id', { count: 'exact', head: true })
      .eq('is_friend', true)
      .eq('hidden', false);
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * افزودنِ دستیِ کسی که در اپ نیست (WHOSUP-148) — قرینه‌ی
   * `contactsRepo.createManual` موبایل، منهای عکس و شبکه‌های اجتماعی که وب
   * نشانشان نمی‌دهد. همان قواعد: `source: 'manual'`، `is_friend` از همان
   * لحظه، `phone_keys` را تریگرِ دیتابیس می‌سازد (مایگریشن 0003)، و شماره‌ی
   * تکراری همان‌جا رد می‌شود (مایگریشن 0048) — نگاه کن به
   * `isDuplicatePhoneError` در فرمِ وب.
   *
   * بعد از درج، مثل موبایل `match_my_contacts` صدا می‌شود: شاید همین آدم
   * همین حالا عضو باشد و باید به حسابش بخورد.
   */
  async create(edit: ContactFriendEdit): Promise<number> {
    const name = edit.name.trim();
    // شماره‌ی بدونِ کد کشور، کد کشورِ خودِ کاربر رو می‌گیره (WHOSUP-162).
    const cc = await phoneRepo.myCallingCode();
    const phones = uniq(edit.phones.map((p) => normalizePhone(p, cc)));
    const emails = uniq(edit.emails.map((e) => normalizeEmail(e)));
    if (isDemoMode) {
      return demoStore.contacts.createManual(
        { name, phones, emails, socials: [], avatarLocalUri: null, avatarPath: null }, null,
      );
    }
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw new Error('not signed in');
    const { data, error } = await supabase.from('contacts').insert({
      owner_id: auth.user.id,
      // همان قالبِ موبایل: یکتا، و معلوم که از دفترچه‌ی هیچ گوشی‌ای نیامده.
      device_id: `manual:${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      source: 'manual',
      is_friend: true,
      display_name: name,
      phones,
      emails,
    }).select('id').single();
    if (error) throw error;
    await rematch();
    return data.id as number;
  },

  /**
   * ویرایشِ اسم/شماره/ایمیلِ همون آدم — قرینه‌ی کوچکِ
   * `contactsRepo.updateContact` موبایل، با همون نرمال‌سازی (ردیف‌های خالی و
   * تکراری حذف می‌شن). عکس و شبکه‌های اجتماعی عمداً دست نمی‌خورن: وب اصلاً
   * نشونشون نمی‌ده و نباید چیزی رو که نمی‌بینه پاک کنه.
   *
   * بعد از ذخیره، مثل موبایل `match_my_contacts` صدا می‌شه: شاید ایمیلِ
   * تازه مالِ کسی باشه که همین الان عضوه. شکستِ مچ، خودِ ویرایش رو نمی‌شکنه.
   */
  async update(id: number, edit: ContactFriendEdit): Promise<void> {
    const name = edit.name.trim();
    const cc = await phoneRepo.myCallingCode();
    const phones = uniq(edit.phones.map((p) => normalizePhone(p, cc)));
    const emails = uniq(edit.emails.map((e) => normalizeEmail(e)));
    if (isDemoMode) return demoStore.contacts.updateBasic(id, name, emails, phones);
    const { error } = await supabase.from('contacts')
      .update({ display_name: name, phones, emails }).eq('id', id);
    if (error) throw error;
    await rematch();
  },

  /**
   * از فهرستِ دوستان برش می‌داره — ولی ردیف رو پاک نمی‌کنه.
   *
   * همون کاری که `contactsRepo.setFriend(id, false)` روی موبایل می‌کنه، و به
   * همون دلیل: شماره و اسمِ این آدم مالِ دفترچه‌ی خودِ کاربره و «دوست نیست»
   * یعنی دوست نیست، نه «فراموشش کن».
   */
  async remove(id: number): Promise<void> {
    if (isDemoMode) return demoStore.contacts.setFriend(id, false);
    const { error } = await supabase.from('contacts').update({ is_friend: false }).eq('id', id);
    if (error) throw error;
  },

  /**
   * هرچه از **دفترچه‌ی گوشی** وارد شده، برای همیشه می‌رود (WHOSUP-169).
   *
   * ═══ چرا این یکی واقعاً `delete` است ═══
   *
   * `remove` بالا عمداً ردیف را نگه می‌دارد، چون «دوست نیست» یعنی دوست
   * نیست. این‌جا حرفِ کاربر چیزِ دیگری است: «آن داده را نداشته باش». یک
   * ردیفِ باقی‌مانده با اسم و شماره‌ی یک آدمِ سوم، جوابِ آن درخواست نیست.
   *
   * ═══ چرا فقط `source = 'device'` ═══
   *
   * دو جور ردیف در این جدول است: آنچه از دفترچه‌ی گوشی همگام شده
   * (`device`) و آنچه خودِ کاربر ساخته — دستی، یا خودکار موقعِ دعوت
   * کردنِ کسی (`manual`، مایگریشن 0075). فقط اولی «داده‌ای که از دستگاهِ
   * کاربر جمع کرده‌ایم» است و همان چیزی که فرمِ Data safety درباره‌اش
   * می‌پرسد. دومی فهرستِ دوستانِ خودِ اوست و پاک کردنش با یک دکمه‌ی
   * «مخاطبینِ واردشده» غافلگیری است، نه خدمت — هر کدامشان دکمه‌ی حذفِ
   * خودش را در فهرستِ دوستان دارد.
   *
   * عکسی برای پاک کردن نمی‌ماند: باکتِ `contact-avatars` فقط از راهِ
   * افزودن/ویرایشِ دستی پر می‌شود، و ردیفِ آمده از دفترچه هیچ عکسی بالا
   * نمی‌فرستد (نگاه کن به `contactsRepo.ensureRow` و `sync_contacts`).
   *
   * `contact_sync_state` هم می‌رود: «آخرین همگام‌سازی: دیروز» وقتی هیچ
   * ردیفِ همگام‌شده‌ای نمانده، فقط گمراه‌کننده است.
   */
  async removeAllImported(): Promise<void> {
    if (isDemoMode) return demoStore.contacts.removeImported();
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw new Error('not signed in');
    const { error } = await supabase.from('contacts')
      .delete().eq('owner_id', auth.user.id).eq('source', 'device');
    if (error) throw error;
    // شکستِ این یکی کارِ اصلی را نشکند: مخاطبین رفته‌اند و همین مهم است.
    const { error: e2 } = await supabase.from('contact_sync_state')
      .delete().eq('user_id', auth.user.id);
    if (e2) reportError('contactFriendRepo.removeAllImported.state', e2);
  },
};
